import { type FC, useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import ChevronIcon from '@assets/img/icons/chevron.svg?react';
import { type NextBuild, type UnionGameItemType } from '@common/types/userType';
import { HandPointer } from '@components/Intro/HandPointer/HandPointer';
import { IntroContext } from '@components/Intro/Intro';
import { selectQuarryByResourceNameOrId } from '@redux/selectors/common';
import { type RootState, useAppSelector } from '@redux/store';
import cn from 'classnames';

import { useCheckRequirements } from '@/common/hooks/check-requirements';
import { getImage } from '@/common/lib/images';
import { getText } from '@/common/lib/text';
import { createRequirementLink } from '@/components/RequirementsItem/requirement-link';
import { StringSmall, StringXSmall } from '@/components/Typography/Typography';
import { Picture } from '@/components/ui/Picture/Picture';
import { selectNextToBuildByBuidlKind } from '@/redux/selectors/common';
import { selectBuildLevelByName } from '@/redux/selectors/userSelectors';

import { HammerMotionIcon } from './HammerMotionIcon';

import css from './styles.module.scss';

const NextBuildItem: FC<NextBuild> = ({ plant, level }) => {
  const { t } = useTranslation();
  const { isIntroActive } = useContext(IntroContext);
  const userLevel = useAppSelector((state) => state.user.user?.level) ?? 0;
  const showPointer = userLevel < 2 && !isIntroActive;
  const nextToBuild = useSelector((state: RootState) =>
    selectNextToBuildByBuidlKind(state, plant),
  );
  const quarryToUpdate = useAppSelector((state) =>
    selectQuarryByResourceNameOrId(state, plant),
  );
  const [check, setCheck] = useState(false);

  const nextToBuildRequirements = nextToBuild
    ? nextToBuild.creationRequirements
    : undefined;
  const quarryRequirements = quarryToUpdate
    ? quarryToUpdate.nextLevelRequirements
    : undefined;

  const nextToBuildCheck = useCheckRequirements(nextToBuildRequirements);
  const quarryCheck = useCheckRequirements(quarryRequirements);

  useEffect(() => {
    if (nextToBuild?.name === plant) {
      setCheck(nextToBuildCheck.isComplete);
    } else {
      setCheck(quarryCheck.isComplete);
    }
  }, [nextToBuild, quarryToUpdate, plant, nextToBuildCheck, quarryCheck]);

  const navigate = useNavigate();
  const plantLevel = useAppSelector((state) =>
    selectBuildLevelByName(state, plant as UnionGameItemType),
  );
  const [plantName, setPlantName] = useState('');

  useEffect(() => {
    const name = textItem();
    setPlantName(name ? name : '');
  }, [plant]);

  const image = getImage({
    name: plant,
    field: 'build',
  });

  const navigateItems = (): void => {
    const link = createRequirementLink({
      value: level,
      type: 'plant',
      name: plant,
      plantLevel: plantLevel || 0,
    });

    if (link) {
      navigate(link, { state: { plant } });
    }
  };

  function textItem(): string | undefined {
    return getText({
      name: plant,
      kind: 'build',
      t,
    });
  }
  const doTask = (): void | (() => void) => {
    const currentTaskId =
      nextToBuild?.name === plant ? nextToBuild.id : quarryToUpdate?.id;

    if (currentTaskId) {
      localStorage.setItem('lastVisitedBuildTaskId', currentTaskId);
    }
    navigateItems();
  };

  return (
    <div className="relative flex flex-1">
      <div
        onClick={doTask}
        className={cn(css.nextBuild, check && css['nextBuild--unvisited'])}
      >
        {image && (
          <Picture
            img={image.img}
            webp={image.webp}
            className="size-10 shrink-0 mr-2"
            alt="task"
          />
        )}
        <div className="flex flex-col gap-[2px] flex-1 min-w-[0%] mr-1">
          <StringXSmall className="!text-white/40">{t('Build')}</StringXSmall>
          <StringSmall
            weight="medium"
            className="overflow-hidden text-ellipsis whitespace-nowrap"
          >
            {plantName}
          </StringSmall>
        </div>

        {check ? (
          <HammerMotionIcon
            animate={{ rotate: ['45deg', '0deg'] }}
            transition={{
              duration: 0.6,
              repeat: Infinity,
              repeatType: 'reverse',
              ease: 'linear',
            }}
            className={cn('size-4 shrink-0 mr-2 text-accent')}
          />
        ) : (
          <ChevronIcon className="text-white/40 size-6 shrink-0" />
        )}
      </div>
      {showPointer && (
        <HandPointer
          className="absolute top-8 left-[60%] w-[72px] h-[72px] z-10"
          style={{ pointerEvents: 'none' }}
          animateOpacity
        />
      )}
    </div>

    // <Container
    //   key={id}
    //   onClick={doTask}
    //   className={cn(
    //     'w-full flex flex-1 items-center justify-between gap-2',
    //     check ? 'border border-accent/60' : 'cursor-pointer',
    //     'taskItemBackground',
    //   )}
    //   style={{ borderWidth: check ? '1.5px' : '' }}
    //   variant="task"
    //   padding="8"
    // >
    //   <div className="colorSegment"></div>
    //   <div className="flex items-center gap-2">
    //     {image && (
    //       <Picture
    //         className="w-[2rem] h-[2rem]"
    //         img={image.img}
    //         webp={image.webp}
    //         alt={plant}
    //       />
    //     )}
    //     <div className="nextBuild_info">
    //       <span>{generateTextBuild()}</span>
    //       <p>{plantName}</p>
    //     </div>
    //   </div>
    //   <div className="flex items-center gap-2">
    //     {check ? (
    //       <div id="animate-hummer">
    //         <img className="w-4 h-4" src="/img/icons/hummer.svg" alt="" />
    //       </div>
    //     ) : (
    //       <InfoIcon className="w-6 h-6 shrink-0" />
    //     )}
    //     <ChevronIcon className="w-6 h-6 shrink-0 text-white/40" />
    //   </div>
    // </Container>
  );
};

export { NextBuildItem };
